import { useEffect, useMemo, useState } from "react";
import { Send, Megaphone, Search, Users, Image as ImageIcon, History, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Broadcast() {
  const [tab, setTab] = useState("compose");
  const [recipients, setRecipients] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [filters, setFilters] = useState({ source: "", status: "", search: "" });
  const [message, setMessage] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [images, setImages] = useState([]);

  const loadRecipients = async () => {
    try {
      const r = await api.get("/broadcasts/recipients", { params: filters });
      setRecipients(r.data || []);
    } catch { toast.error("Could not load recipients"); }
  };
  const loadHistory = async () => {
    try { const r = await api.get("/broadcasts"); setHistory(r.data || []); } catch {}
  };
  const loadImages = async () => {
    try { const r = await api.get("/studio/images"); setImages((r.data || []).filter(i => i.active && i.share_with_guests && i.media_type !== "video" && String(i.url || "").startsWith("http"))); } catch {}
  };

  useEffect(() => { loadRecipients(); loadHistory(); loadImages(); }, []);
  useEffect(() => { loadRecipients(); }, [filters.source, filters.status]);

  const toggleOne = (phone) => {
    const n = new Set(selected);
    n.has(phone) ? n.delete(phone) : n.add(phone);
    setSelected(n);
  };
  const toggleAll = () => {
    if (selected.size === recipients.length) setSelected(new Set());
    else setSelected(new Set(recipients.map(r => r.phone)));
  };

  const send = async () => {
    if (!message.trim()) return toast.error("Message is required");
    if (selected.size === 0) return toast.error("Pick at least one recipient");
    if (!window.confirm(`Send this broadcast to ${selected.size} guest${selected.size > 1 ? "s" : ""} on WhatsApp? This is live — real messages will go out.`)) return;
    setSending(true);
    try {
      await api.post("/broadcasts/send", { message, recipients: Array.from(selected), image_url: imageUrl || "" });
      toast.success(`Queued broadcast to ${selected.size} recipient${selected.size > 1 ? "s" : ""}`);
      setMessage(""); setImageUrl(""); setSelected(new Set());
      setTab("history"); loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  const filtered = useMemo(() => {
    const s = filters.search.trim().toLowerCase();
    if (!s) return recipients;
    return recipients.filter(r => (r.name || "").toLowerCase().includes(s) || (r.phone || "").includes(s));
  }, [recipients, filters.search]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Megaphone className="w-8 h-8 text-[#B84F3E]" strokeWidth={1.5}/>
        <div>
          <h1 className="font-serif text-4xl">WhatsApp Broadcast</h1>
          <p className="text-sm text-[#8A7970]">Send one message to many past guests or leads at once. Smart filters + optional image attachment.</p>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[
          { k: "compose", label: "Compose & Send", icon: Send },
          { k: "history", label: "History", icon: History },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`broadcast-tab-${t.k}`}
            className={`px-4 py-2 rounded-full text-sm border flex items-center gap-2 ${tab === t.k ? "bg-[#B84F3E] text-white border-[#B84F3E]" : "bg-white border-[#E7C2A6] hover:bg-[#F5F2EB]"}`}>
            <t.icon className="w-4 h-4"/>{t.label}
          </button>
        ))}
      </div>

      {tab === "compose" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Message composer */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-xl p-5 border border-[#E7C2A6]/40">
              <h3 className="font-serif text-xl mb-3">Message</h3>
              <Textarea rows={6} value={message} onChange={e => setMessage(e.target.value)}
                placeholder="Example: Hi {{name}}! Weekend getaway deal at Megh Balika — book this weekend and get complimentary breakfast 🍳 Reply YES to grab it 🙏"
                data-testid="broadcast-message"/>
              <div className="text-xs text-[#8A7970] mt-1">Tip: Mention a clear benefit + ask for a reply. Keep it under 400 characters so WhatsApp doesn't truncate.</div>
            </div>

            <div className="bg-white rounded-xl p-5 border border-[#E7C2A6]/40">
              <h3 className="font-serif text-xl mb-3 flex items-center gap-2"><ImageIcon className="w-4 h-4"/>Attach an image (optional)</h3>
              {images.length === 0 ? (
                <div className="text-sm text-[#8A7970]">No guest-shareable images yet. Upload them in <b>AI Studio → Image Library</b> and toggle <b>Guest-shareable</b>.</div>
              ) : (
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                  <button type="button" onClick={() => setImageUrl("")}
                    className={`h-20 rounded border flex items-center justify-center text-xs ${imageUrl === "" ? "border-[#B84F3E] bg-[#FBE8DA]" : "border-[#E7C2A6] bg-white"}`}
                    data-testid="broadcast-image-none">None</button>
                  {images.map(i => (
                    <button key={i.id} type="button" onClick={() => setImageUrl(i.url)}
                      className={`relative h-20 rounded overflow-hidden border ${imageUrl === i.url ? "border-[#B84F3E] ring-2 ring-[#B84F3E]" : "border-[#E7C2A6]"}`}
                      data-testid={`broadcast-image-${i.id}`}
                      title={i.title}>
                      <img src={i.url} alt={i.title} className="w-full h-full object-cover"/>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl p-5 border border-[#E7C2A6]/40 flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-sm font-medium">{selected.size} recipient{selected.size !== 1 ? "s" : ""} selected</div>
                <div className="text-xs text-[#8A7970]">Only guests who messaged your WhatsApp first within 24h can receive free-form messages. For older contacts, use a WhatsApp-approved template.</div>
              </div>
              <Button onClick={send} disabled={sending || selected.size === 0 || !message.trim()} className="bg-[#B84F3E] hover:bg-[#9A3F30]" data-testid="broadcast-send-btn">
                <Send className="w-4 h-4 mr-2"/>{sending ? "Queuing..." : `Send Broadcast`}
              </Button>
            </div>
          </div>

          {/* Recipients */}
          <div className="bg-white rounded-xl border border-[#E7C2A6]/40 flex flex-col" style={{ maxHeight: "75vh" }}>
            <div className="p-4 border-b border-[#E7C2A6]/40">
              <h3 className="font-serif text-lg mb-2 flex items-center gap-2"><Users className="w-4 h-4"/>Recipients</h3>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <select className="h-9 rounded-md border border-[#E7C2A6] bg-white px-2 text-xs" value={filters.source} onChange={e => setFilters({ ...filters, source: e.target.value })} data-testid="filter-source">
                  <option value="">All sources</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="meta_ads">Meta Ads</option>
                  <option value="website">Website</option>
                  <option value="gmb">Google (GMB)</option>
                </select>
                <select className="h-9 rounded-md border border-[#E7C2A6] bg-white px-2 text-xs" value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })} data-testid="filter-status">
                  <option value="">All statuses</option>
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="quoted">Quoted</option>
                  <option value="booked">Booked</option>
                  <option value="lost">Lost</option>
                </select>
              </div>
              <div className="relative">
                <Search className="w-3 h-3 absolute left-2 top-2.5 text-[#8A7970]"/>
                <Input value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })} placeholder="Search name or phone..." className="pl-7 h-9 text-xs" data-testid="filter-search"/>
              </div>
              <div className="flex items-center justify-between mt-2">
                <button onClick={toggleAll} className="text-xs text-[#B84F3E] underline" data-testid="select-all-btn">
                  {selected.size === recipients.length && recipients.length > 0 ? "Unselect all" : "Select all"}
                </button>
                <span className="text-xs text-[#8A7970]">{filtered.length} shown</span>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-[#E7C2A6]/30">
              {filtered.length === 0 && <div className="p-6 text-center text-xs text-[#8A7970]">No recipients match these filters.</div>}
              {filtered.map(r => (
                <label key={r.id || r.phone} className="flex items-center gap-3 p-3 hover:bg-[#F5F2EB] cursor-pointer">
                  <input type="checkbox" checked={selected.has(r.phone)} onChange={() => toggleOne(r.phone)} data-testid={`recipient-${r.phone}`}/>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{r.name || "—"}</div>
                    <div className="text-xs text-[#8A7970]">{r.phone} · {r.source || "n/a"} · {r.status || "new"}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "history" && (
        <div className="bg-white rounded-xl border border-[#E7C2A6]/40 divide-y divide-[#E7C2A6]/30" data-testid="broadcast-history">
          {history.length === 0 && <div className="p-6 text-center text-sm text-[#8A7970]">No broadcasts sent yet.</div>}
          {history.map(h => (
            <div key={h.id} className="p-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-xs text-[#8A7970]">{new Date(h.created_at).toLocaleString()} · by {h.created_by}</div>
                <div className="flex gap-2 text-xs">
                  <span className="px-2 py-0.5 rounded bg-[#F5F2EB]">To: {h.recipients_count}</span>
                  <span className="px-2 py-0.5 rounded bg-[#EEF6EF] text-[#477A5D]">Sent: {h.sent}</span>
                  {!!h.failed && <span className="px-2 py-0.5 rounded bg-[#FBE8DA] text-[#B84F3E]">Failed: {h.failed}</span>}
                  <span className="px-2 py-0.5 rounded bg-[#F5F2EB]">{h.status}</span>
                </div>
              </div>
              <div className="text-sm mt-2 whitespace-pre-wrap">{h.message}</div>
              {h.image_url && <img src={h.image_url} alt="attachment" className="mt-2 h-24 object-cover rounded"/>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
